
import { useState, useRef, useCallback, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { IconRefresh } from "./Icons";

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  threshold?: number;
  className?: string;
}

export function PullToRefresh({
  onRefresh,
  children,
  threshold = 80,
  className,
}: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [phase, setPhase] = useState<"idle" | "pulling" | "ready" | "refreshing">("idle");
  const startY = useRef(0);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging.current || isRefreshing) return;
      if (window.scrollY > 0) {
        isDragging.current = false;
        return;
      }

      const deltaY = e.touches[0].clientY - startY.current;
      if (deltaY < 0) return;

      // Apply resistance — harder to pull as distance increases
      const resistance = 0.4;
      const distance = Math.min(deltaY * resistance, threshold * 1.5);

      setPullDistance(distance);
      setPhase(distance >= threshold ? "ready" : "pulling");
    },
    [isRefreshing, threshold]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    if (pullDistance >= threshold) {
      setPhase("refreshing");
      setIsRefreshing(true);
      setPullDistance(50);

      try {
        await onRefresh();
      } finally {
        setPullDistance(0);
        setIsRefreshing(false);
        setPhase("idle");
      }
    } else {
      setPullDistance(0);
      setPhase("idle");
    }
  }, [pullDistance, threshold, onRefresh]);

  const indicatorProgress = Math.min(pullDistance / threshold, 1);

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <AnimatePresence>
        {phase !== "idle" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
            style={{ transform: `translateX(-50%) translateY(${Math.max(pullDistance - 40, -40)}px)` }}
          >
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                "bg-surface-elevated border border-border shadow-md",
                "transition-colors duration-200",
                phase === "ready" || phase === "refreshing" ? "text-brand" : "text-text-subtle"
              )}
            >
              <motion.span
                animate={{
                  rotate: phase === "refreshing" ? 360 : indicatorProgress * 270,
                  scale: phase === "ready" ? 1.1 : 1,
                }}
                transition={
                  phase === "refreshing"
                    ? { duration: 0.8, repeat: Infinity, ease: "linear" }
                    : { duration: 0 }
                }
              >
                <IconRefresh className="w-5 h-5" />
              </motion.span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with pull offset */}
      <div
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: isDragging.current ? undefined : "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
